import { useState, useEffect, useCallback } from "react";
import { api } from "../services/api";
import type { FeedItem } from "../types/Post/post";

interface UseFeedResult {
  items: FeedItem[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  refresh: () => void;
  updateItem: (postId: string, updater: (item: FeedItem) => FeedItem) => void;
  removeItem: (postId: string) => void;
  prependItems: (newItems: FeedItem[]) => void;
}

/**
 * Hook for managing feed state with cursor-based pagination
 * 
 * Features:
 * - Fetches initial feed on mount
 * - Supports infinite scroll via loadMore
 * - Deduplicates items by postId
 * - Provides refresh functionality
 * 
 * @returns Feed state and control functions
 */
export function useFeed(): UseFeedResult {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(false);

  /**
   * Fetches feed data from the API
   * @param cursorValue - Optional cursor for pagination
   * @param append - Whether to append results or replace
   */
  const fetchFeed = useCallback(async (cursorValue: string | null, append: boolean) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.social.getFeed(cursorValue || undefined);
      
      if (append) {
        // Append new items, deduplicating by post._id or postId
        setItems((prevItems) => {
          const existingIds = new Set(prevItems.map((item) => item.post._id));
          const newItems = response.items.filter((item) => !existingIds.has(item.post._id));
          return [...prevItems, ...newItems];
        });
      } else {
        // Replace items (initial load or refresh)
        setItems(response.items);
      }

      setCursor(response.cursor);
      setHasMore(response.hasMore);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load feed";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load more items using the current cursor
   * Guarded by hasMore and loading state
   */
  const loadMore = useCallback(() => {
    if (!hasMore || loading) {
      return;
    }
    fetchFeed(cursor, true);
  }, [hasMore, loading, cursor, fetchFeed]);

  /**
   * Refresh the feed from the beginning
   * Clears existing items and resets cursor
   */
  const refresh = useCallback(() => {
    setCursor(null);
    setItems([]);
    fetchFeed(null, false);
  }, [fetchFeed]);

  /**
   * Update a single feed item by postId
   * @param postId - The post to update
   * @param updater - Function that returns the updated item
   */
  const updateItem = useCallback((postId: string, updater: (item: FeedItem) => FeedItem) => {
    setItems((prev) => prev.map((item) => (item.post._id === postId ? updater(item) : item)));
  }, []);

  /**
   * Remove a feed item by postId
   */
  const removeItem = useCallback((postId: string) => {
    setItems((prev) => prev.filter((item) => item.post._id !== postId));
  }, []);

  /**
   * Prepend new items to the top of the feed, deduplicating by post._id.
   * Used when loading new posts that arrived since last check.
   */
  const prependItems = useCallback((newItems: FeedItem[]) => {
    setItems((prev) => {
      const existingIds = new Set(prev.map((item) => item.post._id));
      const unique = newItems.filter((item) => !existingIds.has(item.post._id));
      if (unique.length === 0) return prev;
      return [...unique, ...prev];
    });
  }, []);

  // Fetch initial feed on mount
  useEffect(() => {
    fetchFeed(null, false);
  }, [fetchFeed]);

  return {
    items,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
    updateItem,
    removeItem,
    prependItems,
  };
}
